import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  OnDestroy,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EMPTY, Observable, Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { ICONS, Icon } from '@nimbus-library/features/shared/resources/icons';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { IconWeight } from '@nimbus-library/features/shared/directives/icon-weight.enum';
import { FileService } from '@nimbus-library/features/shared/services/helper/file.service';
import { ChartTheme } from '@nimbus-library/features/charts/models/char-theme.enum';
import { CommonThemeState } from '@nimbus-library/features/shared/store/theme-store/common-theme.state';
import { ChatServiceType } from '@nimbus-global/features/chat/models/chat.model';
import { ChatMediaPolicy } from '@nimbus-global/features/chat/models/chat-service.model';
import { IConversationMediaAllowedFileType } from '@nimbus-global/features/chat/models/chat-twilio.model';
import { getSmsSegmentsAndRemaining, SmsEncodingName } from '@nimbus-global/features/chat/utils/sms-counter.util';
import { getChatAttachmentIcon } from '@nimbus-global/features/chat/utils/chat-attachment-icon.util';
import { UserInteractionOutcome } from '@nimbus-library/features/agent-assistant/models/agent-assistant.model';
import { ChatDraftService } from '@nimbus-global/features/chat/services/chat-draft.service';
import { isChatInputDisabledForTaskState } from '@nimbus-global/features/chat/utils/chat-enabled.utils';
import { ChatVoiceRecorderService, VoiceRecordingState } from '@nimbus-global/features/chat/services/chat-voice-recorder.service';
import { MediaChannel } from '@nimbus-global/features/manage/model/file-type-policy.enum';
import {
  CHAT_LINE_PREFIX_MARKERS,
  CHAT_WRAP_MARKERS,
  ChatFormatType,
  continueListOnEnter,
  ITextEditResult,
  isLineFormatActive,
  isWrapTokenActive,
  isWrapTypeBlockedByConflict,
  prefixSelectionLines,
  toggleWrapOnSelection,
  toggleLinePrefixAtCaret,
  toggleWrapAtCaret,
} from './chat-input-formatting.util';
import type { ChatEmojiPickerComponent } from './emoji-picker/chat-emoji-picker.component';
import { IEmojiPickerI18n } from './emoji-picker/emoji-picker-i18n.model';

@UntilDestroy()
@Component({
  selector: 'nimbus-chat-input-message',
  standalone: false,
  templateUrl: './chat-input-message.component.html',
  styleUrl: './chat-input-message.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatInputMessageComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('messageTextarea') private textareaRef: ElementRef<HTMLTextAreaElement>;
  @ViewChild('attachmentFileInput') private attachmentFileInputRef: ElementRef<HTMLInputElement>;
  @ViewChild('emojiPickerContainer', { read: ViewContainerRef }) private emojiPickerContainerRef?: ViewContainerRef;
  @ViewChild('emojiPickerToggle') private emojiPickerToggleRef?: ElementRef<HTMLButtonElement>;

  @Input() public chatType: ChatServiceType;
  @Input() public inputPlaceholderTKey: TranslationKeys;
  @Input() public saveDraftApiUrl: string;
  @Input() public conversationId: string;
  @Input() public enableDraft = false;
  @Input() public isMediaHandlingEnabled: boolean | null = false;
  @Input() public mediaPolicy: ChatMediaPolicy | null = null;
  @Input() public isCustomerChat = true;

  @Output() public messageSent = new EventEmitter<string>();
  @Output() public voiceMessageSent = new EventEmitter<Blob>();
  @Output() public attachmentsSent = new EventEmitter<File[]>();
  @Output() public stopRequested = new EventEmitter<void>();

  private readonly attachmentIcons = new Map<File, string>();
  private readonly maxRows = 3;
  private taskDisabled = false;
  private inputDisabledValue = false;
  private showStopButtonValue = false;
  private lastTaskState?: UserInteractionOutcome;
  private readonly secondsPerMinute = 60;
  private readonly timePadLength = 2;
  private readonly smsUcs2MaxMessageLength = 700;
  private readonly maxMessageLength = 1600;
  private cachedLineHeight?: number;
  // properties to handle resize observer and avoid unnecessary calls to resizeTextarea
  private resizeObserver?: ResizeObserver;
  private lastObservedWidth?: number;
  private resizeReqAnimFrameId?: number;
  private emojiPickerRef?: ComponentRef<ChatEmojiPickerComponent>;
  private emojiPickerSubscription?: Subscription;

  protected readonly IconWeight = IconWeight;
  protected readonly VoiceRecordingState = VoiceRecordingState;

  public pendingAttachments: File[] = [];
  public readonly ICONS = ICONS;
  public readonly TranslationKeys = TranslationKeys;
  public readonly chatFormatToggleIconPath = '/images/chat-format-toggle.svg';
  public readonly formatToolbarButtons: ReadonlyArray<{ type: ChatFormatType; icon: Icon; labelKey: TranslationKeys }> = [
    { type: 'bold', icon: ICONS.BOLD, labelKey: TranslationKeys.ChatWidgetFormatBold },
    { type: 'italic', icon: ICONS.ITALIC, labelKey: TranslationKeys.ChatWidgetFormatItalic },
    { type: 'strikethrough', icon: ICONS.STRIKE_THROUGH, labelKey: TranslationKeys.ChatWidgetFormatStrikethrough },
    { type: 'code', icon: ICONS.COMPILER, labelKey: TranslationKeys.ChatWidgetFormatCode },
    { type: 'monospace', icon: ICONS.MONOSPACE, labelKey: TranslationKeys.ChatWidgetFormatMonospace },
    { type: 'numbered-list', icon: ICONS.NUMBERING, labelKey: TranslationKeys.ChatWidgetFormatNumberedList },
    { type: 'bulleted-list', icon: ICONS.BULLETS, labelKey: TranslationKeys.ChatWidgetFormatBulletedList },
    { type: 'quote', icon: ICONS.QUOTE, labelKey: TranslationKeys.ChatWidgetFormatQuote },
  ];

  public messageControl = new FormControl('', [Validators.maxLength(this.maxMessageLength), this.smsMaxLengthValidator()]);
  public showCounter = false;
  public canSend = false;
  public smsSegmentsCount = 1;
  public smsCharsRemaining: number = 160;
  public messageLimitExceededBy = 0;
  public disabled = false;
  public isParked = false;
  public showEmojiPicker = false;
  public emojiPickerI18n: IEmojiPickerI18n = { categories: {} };
  public showFormattingOptions: boolean = false;
  public activeFormats = new Set<ChatFormatType>();

  public get isAttachmentSupported(): boolean {
    return !!this.mediaPolicy?.allowedFileTypes?.length;
  }

  public get isDarkMode(): boolean {
    return this.store.selectSnapshot(CommonThemeState.theme) === ChartTheme.Dark;
  }

  public get isVoiceRecordingSupported(): boolean {
    return (
      [ChatServiceType.Whatsapp, ChatServiceType.Rcs].includes(this.chatType) &&
      !!this.mediaPolicy?.allowedFileTypes?.find((allowedFileType) => allowedFileType.mimeTypes.includes('audio/ogg'))
    );
  }

  public get isRecordingDisabled(): boolean {
    return this.disabled || this.pendingAttachments.length > 0;
  }

  public get isFormattingSupported(): boolean {
    return this.chatType === ChatServiceType.Whatsapp;
  }

  public get isExpanded(): boolean {
    return this.showFormattingOptions && this.isFormattingSupported && this.voiceRecorder.state$.value === VoiceRecordingState.Idle;
  }

  public get acceptedFileTypes(): string | null {
    if (!this.mediaPolicy?.allowedFileTypes?.length) {
      return null;
    }
    return this.mediaPolicy.allowedFileTypes
      .flatMap((allowedFileType) => [...allowedFileType.mimeTypes, ...allowedFileType.extensions])
      .join(',');
  }

  public readonly recordingTime$: Observable<string> = this.voiceRecorder.seconds$.pipe(
    map(
      (s) =>
        `${Math.floor(s / this.secondsPerMinute)
          .toString()
          .padStart(this.timePadLength, '0')}:${(s % this.secondsPerMinute).toString().padStart(this.timePadLength, '0')}`,
    ),
  );

  @Input()
  public set taskState(state: UserInteractionOutcome) {
    const isACWState = [UserInteractionOutcome.InAfterCallWork, UserInteractionOutcome.InExtendedAfterCallWork].includes(state);
    const isParkedState = [UserInteractionOutcome.Parked].includes(state);
    this.isParked = isParkedState;

    if (isParkedState && this.lastTaskState && this.lastTaskState !== UserInteractionOutcome.Parked) {
      this.saveDraftMessage();
      this.stopActiveRecording();
    }
    this.lastTaskState = state;

    this.inputPlaceholderTKey = this.getInputPlaceholderTKey(state);
    if (isACWState) {
      this.messageControl.setValue('');
    }

    this.taskDisabled = isChatInputDisabledForTaskState(state);
    this.applyDisabledState();
    this.resizeTextarea();
  }

  @Input()
  public set inputDisabled(value: boolean) {
    this.inputDisabledValue = value;
    this.applyDisabledState();
  }

  @Input()
  public set showStopButton(value: boolean) {
    this.showStopButtonValue = value;
    this.updateCanSend();
  }

  public get showStopButton(): boolean {
    return this.showStopButtonValue;
  }

  public constructor(
    private readonly chatDraftService: ChatDraftService,
    private readonly fileService: FileService,
    private readonly store: Store,
    private readonly translateService: TranslateService,
    private readonly toastrService: ToastrService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    public readonly voiceRecorder: ChatVoiceRecorderService,
  ) {}

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    if (!this.showEmojiPicker) {
      return;
    }

    const target = event.target;
    if (!(target instanceof Node)) {
      return;
    }

    const isClickInsidePickerContainer = this.emojiPickerContainerRef?.element.nativeElement.contains(target) ?? false;
    const isClickInsidePickerHost = this.emojiPickerRef?.location.nativeElement.contains(target) ?? false;
    const isClickOnToggle = this.emojiPickerToggleRef?.nativeElement.contains(target) ?? false;
    if (!isClickInsidePickerContainer && !isClickInsidePickerHost && !isClickOnToggle) {
      this.closeEmojiPicker();
    }
  }

  public ngOnInit(): void {
    this.showCounter = this.chatType === ChatServiceType.Sms;
    const draft = this.enableDraft ? this.chatDraftService.getCurrentDraftText() : '';
    if (draft) {
      this.messageControl.setValue(draft, { emitEvent: false });
    }
    this.messageControl.updateValueAndValidity({ emitEvent: false });
    this.updateCounters();
    this.updateCanSend();
    this.updateEmojiPickerI18n();
    this.translateService.onLangChange.pipe(untilDestroyed(this)).subscribe(() => {
      this.updateEmojiPickerI18n();
    });
  }

  public ngAfterViewInit(): void {
    this.updateCachedLineHeight();
    this.resizeTextarea();
    this.messageControl.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.resizeTextarea();
      this.updateCounters();
      this.updateCanSend();
    });
    this.resizeObserverHandling();
  }

  public ngOnDestroy(): void {
    if (this.lastTaskState !== UserInteractionOutcome.Parked) {
      this.saveDraftMessage();
    }
    this.resizeObserver?.disconnect();
    if (this.resizeReqAnimFrameId !== undefined) {
      cancelAnimationFrame(this.resizeReqAnimFrameId);
    }
    this.voiceRecorder.discard();
    this.closeEmojiPicker();
  }

  public sendMessage(): void {
    if (this.disabled || this.messageControl.invalid || !this.canSend) {
      return;
    }
    const normalizedValue = this.normalizeMessage(this.messageControl.value ?? '');
    if (normalizedValue) {
      this.messageSent.emit(normalizedValue);
      this.messageControl.reset();
      this.resizeTextarea();
      this.updateCounters();
      this.updateActiveFormats();
    }
    if (this.pendingAttachments.length > 0) {
      this.attachmentsSent.emit(this.pendingAttachments);
      this.pendingAttachments = [];
      this.attachmentIcons.clear();
    }
    this.updateCanSend();
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      if (this.isFormattingSupported && this.continueListOnEnter()) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      if (!this.disabled) {
        this.sendMessage();
      }
    }
  }

  public resizeTextarea(): void {
    const el = this.textareaRef?.nativeElement;
    if (!el) {
      return;
    }
    el.style.height = 'auto';

    const { newHeight, overflow } = this.calculateHeight(el);
    el.style.height = `${newHeight}px`;
    el.style.overflowY = overflow;
  }

  public startRecording(): void {
    if (this.isRecordingDisabled) {
      return;
    }
    this.closeEmojiPicker();
    void this.voiceRecorder.start();
  }

  public stopRecording(): void {
    this.voiceRecorder.stop();
  }

  public onStopClick(): void {
    this.stopRequested.emit();
  }

  public sendVoiceMessage(): void {
    if (this.disabled) {
      return;
    }
    const blob = this.voiceRecorder.blob$.value;
    if (blob) {
      this.voiceMessageSent.emit(blob);
      this.voiceRecorder.discard();
    }
  }

  public discardRecording(): void {
    this.voiceRecorder.discard();
  }

  public triggerAttachmentPicker(): void {
    if (this.disabled) {
      return;
    }
    this.attachmentFileInputRef?.nativeElement.click();
  }

  public onAttachmentsSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    this.addAttachments(files);
    input.value = '';
  }

  public onPaste(event: ClipboardEvent): void {
    if (!this.isMediaHandlingEnabled || this.disabled) {
      return;
    }
    const pastedFiles = Array.from(event.clipboardData?.items ?? [])
      .filter((item) => item.kind === 'file')
      .map((item) => item.getAsFile())
      .filter((file): file is File => !!file);
    if (pastedFiles.length > 0) {
      event.preventDefault();
      this.addAttachments(pastedFiles);
    }
  }

  public addEmoji(emoji: string): void {
    if (this.disabled || this.isParked || !emoji) {
      return;
    }

    const currentValue = this.messageControl.value ?? '';
    const textarea = this.textareaRef?.nativeElement;
    const selectionStart = textarea ? (textarea.selectionStart ?? currentValue.length) : currentValue.length;
    const selectionEnd = textarea ? (textarea.selectionEnd ?? selectionStart) : selectionStart;
    const nextValue = `${currentValue.slice(0, selectionStart)}${emoji}${currentValue.slice(selectionEnd)}`;

    this.messageControl.setValue(nextValue, { emitEvent: true });
    this.updateCounters();
    this.updateCanSend();

    if (textarea) {
      queueMicrotask(() => {
        const nextCaretPosition = selectionStart + emoji.length;
        textarea.focus();
        textarea.setSelectionRange(nextCaretPosition, nextCaretPosition);
        this.resizeTextarea();
      });
    }
  }

  public addAttachments(files: File[]): void {
    if (this.disabled) {
      return;
    }
    const validFiles = files.filter((file) => this.validateAttachment(file));
    validFiles.forEach((file) => this.attachmentIcons.set(file, this.resolveAttachmentIcon(file)));
    this.pendingAttachments = [...this.pendingAttachments, ...validFiles];
    this.updateCanSend();
    this.changeDetectorRef.markForCheck();
  }

  public applyFormatting(type: ChatFormatType): void {
    if (this.disabled || this.isParked || !this.isFormattingSupported) {
      return;
    }

    const textarea = this.textareaRef?.nativeElement;
    const currentValue = this.messageControl.value ?? '';
    const selectionStart = textarea ? (textarea.selectionStart ?? currentValue.length) : currentValue.length;
    const selectionEnd = textarea ? (textarea.selectionEnd ?? selectionStart) : selectionStart;
    const hasSelection = selectionStart !== selectionEnd;
    const wrap = CHAT_WRAP_MARKERS[type];

    if (isWrapTypeBlockedByConflict(currentValue, selectionStart, selectionEnd, type)) {
      return;
    }

    let result: ITextEditResult;
    if (wrap) {
      result = hasSelection
        ? toggleWrapOnSelection(currentValue, selectionStart, selectionEnd, wrap)
        : toggleWrapAtCaret(currentValue, selectionStart, wrap);
    } else {
      result = hasSelection
        ? prefixSelectionLines(currentValue, selectionStart, selectionEnd, type)
        : toggleLinePrefixAtCaret(currentValue, selectionStart, type);
    }
    const { nextValue, nextCaretStart, nextCaretEnd } = result;

    this.messageControl.setValue(nextValue, { emitEvent: true });
    this.updateCounters();
    this.updateCanSend();

    if (textarea) {
      queueMicrotask(() => {
        textarea.focus();
        textarea.setSelectionRange(nextCaretStart, nextCaretEnd);
        this.resizeTextarea();
        this.updateActiveFormats();
      });
    }
  }

  public updateActiveFormats(): void {
    const textarea = this.textareaRef?.nativeElement;
    if (!textarea) {
      return;
    }
    const value = this.messageControl.value ?? '';
    const selectionStart = textarea.selectionStart ?? value.length;
    const selectionEnd = textarea.selectionEnd ?? selectionStart;
    const next = new Set<ChatFormatType>();

    (Object.keys(CHAT_WRAP_MARKERS) as ChatFormatType[]).forEach((type) => {
      if (isWrapTokenActive(value, selectionStart, selectionEnd, type)) {
        next.add(type);
      }
    });

    (Object.keys(CHAT_LINE_PREFIX_MARKERS) as ChatFormatType[]).concat('numbered-list').forEach((type) => {
      if (isLineFormatActive(value, selectionStart, type)) {
        next.add(type);
      }
    });

    this.activeFormats = next;
    this.changeDetectorRef.markForCheck();
  }

  public removeAttachment(index: number): void {
    const removedFile = this.pendingAttachments[index];
    this.pendingAttachments = this.pendingAttachments.filter((_, i) => i !== index);
    this.attachmentIcons.delete(removedFile);
    this.updateCanSend();
  }

  public getAttachmentSize(file: File): string {
    return this.fileService.formatFileSize(file.size);
  }

  public getAttachmentIcon(file: File): string {
    return this.attachmentIcons.get(file) ?? '';
  }

  public toggleEmojiPicker(): void {
    if (this.disabled || this.isParked) {
      return;
    }

    if (this.showEmojiPicker) {
      this.closeEmojiPicker();
      return;
    }

    this.showEmojiPicker = true;
    // Renders the host container synchronously so the picker can be attached to it right away.
    this.changeDetectorRef.detectChanges();
    void this.loadEmojiPicker();
  }

  protected toggleFormattingOptions(): void {
    if (this.disabled || this.isParked || !this.isFormattingSupported) {
      return;
    }

    if (this.showFormattingOptions) {
      this.showFormattingOptions = false;
      return;
    }

    this.showFormattingOptions = true;
    queueMicrotask(() => this.updateActiveFormats());
  }

  private updateEmojiPickerI18n(): void {
    this.emojiPickerI18n = {
      categories: {
        activity: this.translateService.instant(TranslationKeys.ChatWidgetEmojiCategoryActivity),
        custom: this.translateService.instant(TranslationKeys.ChatWidgetEmojiCategoryCustom),
        flags: this.translateService.instant(TranslationKeys.ChatWidgetEmojiCategoryFlags),
        foods: this.translateService.instant(TranslationKeys.ChatWidgetEmojiCategoryFoods),
        nature: this.translateService.instant(TranslationKeys.ChatWidgetEmojiCategoryNature),
        objects: this.translateService.instant(TranslationKeys.ChatWidgetEmojiCategoryObjects),
        people: this.translateService.instant(TranslationKeys.ChatWidgetEmojiCategoryPeople),
        places: this.translateService.instant(TranslationKeys.ChatWidgetEmojiCategoryPlaces),
        recent: this.translateService.instant(TranslationKeys.ChatWidgetEmojiCategoryRecent),
        search: this.translateService.instant(TranslationKeys.ChatWidgetEmojiCategorySearchResults),
        symbols: this.translateService.instant(TranslationKeys.ChatWidgetEmojiCategorySymbols),
      },
    };
    this.applyEmojiPickerInputs();
    this.changeDetectorRef.markForCheck();
  }

  /**
   * The picker and its stylesheet are pulled in on demand so that they stay out of the eagerly loaded bundles.
   */
  private async loadEmojiPicker(): Promise<void> {
    const containerRef = this.emojiPickerContainerRef;
    if (!containerRef) {
      return;
    }

    const { ChatEmojiPickerComponent } = await import('./emoji-picker/chat-emoji-picker.component');
    if (!this.showEmojiPicker || this.emojiPickerContainerRef !== containerRef) {
      return;
    }

    this.destroyEmojiPicker();
    this.emojiPickerRef = containerRef.createComponent(ChatEmojiPickerComponent);
    this.emojiPickerSubscription = this.emojiPickerRef.instance.emojiSelected
      .pipe(untilDestroyed(this))
      .subscribe((emoji: string) => this.addEmoji(emoji));
    this.applyEmojiPickerInputs();
    this.changeDetectorRef.markForCheck();
  }

  private applyEmojiPickerInputs(): void {
    if (!this.emojiPickerRef) {
      return;
    }

    this.emojiPickerRef.setInput('darkMode', this.isDarkMode);
    this.emojiPickerRef.setInput('pickerTitle', this.translateService.instant(TranslationKeys.ChatWidgetEmojiPickerTitle));
    this.emojiPickerRef.setInput('pickerI18n', this.emojiPickerI18n);
  }

  private destroyEmojiPicker(): void {
    this.emojiPickerSubscription?.unsubscribe();
    this.emojiPickerSubscription = undefined;
    this.emojiPickerContainerRef?.clear();
    this.emojiPickerRef = undefined;
  }

  private closeEmojiPicker(): void {
    this.showEmojiPicker = false;
    this.destroyEmojiPicker();
  }

  private validateAttachment(file: File): boolean {
    const allowedFileType = this.findAllowedFileType(file);
    if (!allowedFileType) {
      const channelFormat = [MediaChannel.Rcs, MediaChannel.Mms].includes(this.mediaPolicy?.channel)
        ? this.mediaPolicy?.channel.toUpperCase()
        : this.mediaPolicy?.channel;
      this.toastrService.error(
        this.translateService.instant(TranslationKeys.ChatWidgetAttachmentTypeNotSupported, {
          fileName: file.name,
          channel: channelFormat,
        }),
      );
      return false;
    }
    if (file.size > allowedFileType.maxSizeBytes) {
      this.toastrService.error(this.translateService.instant(TranslationKeys.ChatWidgetAttachmentTooLarge, { fileName: file.name }));
      return false;
    }
    return true;
  }

  private findAllowedFileType(file: File): IConversationMediaAllowedFileType | undefined {
    const extension = this.getFileExtension(file.name);
    return this.mediaPolicy?.allowedFileTypes.find(
      (allowedFileType) => allowedFileType.mimeTypes.includes(file.type) || allowedFileType.extensions.includes(extension),
    );
  }

  private getFileExtension(filename: string): string {
    const match = /\.[a-zA-Z0-9]+$/.exec(filename);
    return match ? match[0].toLowerCase() : '';
  }

  private resolveAttachmentIcon(file: File): string {
    const theme = this.store.selectSnapshot(CommonThemeState.theme);
    const themeFolder = theme === ChartTheme.Dark ? 'dark' : 'light';
    return getChatAttachmentIcon(file.type, file.name, themeFolder);
  }

  // Keep DOM/caret orchestration in the component; pure text transforms live in chat-input-formatting.util.ts.
  private continueListOnEnter(): boolean {
    const textarea = this.textareaRef?.nativeElement;
    if (!textarea) {
      return false;
    }

    const value = this.messageControl.value ?? '';
    const selectionStart = textarea.selectionStart ?? value.length;
    const selectionEnd = textarea.selectionEnd ?? selectionStart;
    const result = continueListOnEnter(value, selectionStart, selectionEnd);
    if (!result) {
      return false;
    }

    this.messageControl.setValue(result.nextValue, { emitEvent: true });
    this.updateCounters();
    this.updateCanSend();

    queueMicrotask(() => {
      textarea.focus();
      textarea.setSelectionRange(result.nextCaret, result.nextCaret);
      this.resizeTextarea();
      this.updateActiveFormats();
    });

    return true;
  }

  private stopActiveRecording(): void {
    if (this.voiceRecorder.state$.value === VoiceRecordingState.Recording) {
      this.stopRecording();
    }
  }

  private saveDraftMessage(): void {
    if (!this.enableDraft) {
      return;
    }
    const message = this.messageControl.value ?? '';
    this.chatDraftService
      .saveDraft(this.saveDraftApiUrl, this.conversationId, message)
      .pipe(catchError(() => EMPTY))
      // eslint-disable-next-line nimbus/prefer-until-destroyed -- fire-and-forget: HTTP observable self-completes, intentionally not tied to component lifetime
      .subscribe();
  }

  private getInputPlaceholderTKey(state: UserInteractionOutcome): TranslationKeys {
    switch (state) {
      case UserInteractionOutcome.Parked:
        return TranslationKeys.ChatWidgetInputMessageDisabledPlaceholder;
      case UserInteractionOutcome.InAfterCallWork:
      case UserInteractionOutcome.InExtendedAfterCallWork:
        return TranslationKeys.ChatWidgetInputMessageEndedSessionPlaceholder;
      default:
        return TranslationKeys.ChatWidgetInputMessagePlaceholder;
    }
  }

  private calculateHeight(el: HTMLTextAreaElement): { newHeight: number; overflow: 'auto' | 'hidden' } {
    if (!this.cachedLineHeight) {
      this.updateCachedLineHeight();
    }

    const lineHeight = this.cachedLineHeight ?? el.scrollHeight;
    const maxTotalHeight = lineHeight * this.maxRows;

    const rawScrollHeight = el.scrollHeight;
    const tolerance = 1;
    const maxHeightCeil = Math.ceil(maxTotalHeight + tolerance);

    const clamped = Math.max(lineHeight, Math.min(rawScrollHeight, maxHeightCeil));

    return {
      newHeight: clamped,
      overflow: rawScrollHeight > maxHeightCeil ? 'auto' : 'hidden',
    };
  }

  private updateCachedLineHeight(): void {
    const el = this.textareaRef?.nativeElement;
    if (!el) {
      return;
    }
    const styles = window.getComputedStyle(el);
    const computedLineHeight = parseFloat(styles.lineHeight);
    if (!Number.isNaN(computedLineHeight)) {
      this.cachedLineHeight = computedLineHeight;
    }
  }

  private applyDisabledState(): void {
    const newDisabled = this.taskDisabled || this.inputDisabledValue;
    if (this.disabled === newDisabled) {
      return;
    }
    this.disabled = newDisabled;
    this.updateControlState();
    this.updateCanSend();

    if (this.disabled && this.showEmojiPicker) {
      this.closeEmojiPicker();
    }
  }

  private updateControlState(): void {
    if (this.disabled) {
      this.messageControl.disable({ emitEvent: false });
    } else {
      this.messageControl.enable({ emitEvent: false });
    }
  }

  /* Normalize message like WhatsApp, refined per requirements:
   - Remove spaces/tabs only before the first word (do NOT remove leading blank lines)
   - Preserve empty lines between content lines
   - Remove trailing lines that are only whitespace (no further full line after them)
   */
  private normalizeMessage(text: string): string {
    if (!text) {
      return '';
    }
    let result = text.replace(/^[\t ]+/, '');
    const lines = result.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
    let lastNonEmptyIndex = -1;
    for (let i = lines.length - 1; i >= 0; i--) {
      if (lines[i].trim().length > 0) {
        lastNonEmptyIndex = i;
        break;
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    if (lastNonEmptyIndex === -1) {
      return '';
    }
    const keptLines = lines.slice(0, lastNonEmptyIndex + 1);
    result = keptLines.join('\n').replace(/[ \t]+$/, '');

    return result;
  }

  private updateCanSend(): void {
    const normalized = this.normalizeMessage(this.messageControl.value ?? '');
    this.canSend =
      (!!normalized || this.pendingAttachments.length > 0) && !this.disabled && !this.showStopButtonValue && this.messageControl.valid;
  }

  private updateCounters(): void {
    this.updateMessageLimitExceededBy();
    this.updateSmsCounter();
  }

  private updateMessageLimitExceededBy(): void {
    const rawMessage = (this.messageControl.value ?? '') as string;
    if (!this.showCounter) {
      this.messageLimitExceededBy = this.maxMessageLength - rawMessage.length;

      return;
    }

    const { charactersCount, encodingName } = getSmsSegmentsAndRemaining(rawMessage);
    const smsMaxMessageLength = this.getMaxAllowedMessageLength(encodingName);
    this.messageLimitExceededBy = smsMaxMessageLength - charactersCount;
  }

  private updateSmsCounter(): void {
    if (!this.showCounter) {
      return;
    }

    const rawMessage = (this.messageControl.value ?? '') as string;
    const { segments, remaining } = getSmsSegmentsAndRemaining(rawMessage);

    this.smsSegmentsCount = segments;
    this.smsCharsRemaining = remaining;
  }

  private smsMaxLengthValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.chatType !== ChatServiceType.Sms) {
        return null;
      }

      const { charactersCount, encodingName } = getSmsSegmentsAndRemaining((control.value ?? '') as string);
      const maxAllowedMessageLength = this.getMaxAllowedMessageLength(encodingName);

      return charactersCount > maxAllowedMessageLength
        ? {
            smsMaxLength: {
              actualLength: charactersCount,
              encodingName,
              requiredLength: maxAllowedMessageLength,
            },
          }
        : null;
    };
  }

  private getMaxAllowedMessageLength(encodingName: SmsEncodingName): number {
    return this.chatType === ChatServiceType.Sms && encodingName === 'UCS-2' ? this.smsUcs2MaxMessageLength : this.maxMessageLength;
  }

  // Debounces textarea resize work to the next animation frame.
  private scheduleResize(): void {
    if (this.resizeReqAnimFrameId !== undefined) {
      cancelAnimationFrame(this.resizeReqAnimFrameId);
    }
    this.resizeReqAnimFrameId = requestAnimationFrame(() => {
      this.resizeReqAnimFrameId = undefined;
      this.resizeTextarea();
    });
  }

  // Watches the parent container width and triggers a scheduled resize only when the width changes
  private resizeObserverHandling(): void {
    if (typeof ResizeObserver !== 'undefined') {
      const target = this.textareaRef?.nativeElement?.parentElement;
      if (target) {
        this.resizeObserver = new ResizeObserver((entries) => {
          const entry = entries[0];
          const width = entry?.contentRect?.width ?? target.clientWidth;
          if (this.lastObservedWidth === width) {
            return;
          }
          this.lastObservedWidth = width;
          this.updateCachedLineHeight();
          this.scheduleResize();
        });
        this.resizeObserver.observe(target);
      }
    }
  }
}
